import { KeyboardReturn } from '@mui/icons-material'
import { IconButton, Skeleton, Stack, TextField, useTheme } from '@mui/material'
import { useState } from 'react'
import { toast } from 'react-toastify'

import useGetAgentCentralized from '~/features/agents/hooks/useGetAgentCentralized'
import { EAgentKeys } from '~/features/agents/models'
import useOfuSimulateMessages from '~/features/campaigns/hooks/useOfuSimulateMessages'
import {
  ECampaignChannel,
  TCampaignCreatePayload,
  TOfuSimulateContact,
  TOfuSimulateContactMessages,
  TOfuSimulateMessage,
} from '~/features/campaigns/models'
import { ESpeaker } from '~/features/inbox/models'
import rem from '~/utils/ui'

type TOFUSimulateStepAddConversationItemProps = {
  handleSave: (message: TOfuSimulateMessage[]) => void
  messages: TOfuSimulateMessage[]
  channel: ECampaignChannel
  contact: TOfuSimulateContact
  campaign: TCampaignCreatePayload
  disabled?: boolean
}

export default function OFUSimulateStepAddConversationItem({
  handleSave,
  messages,
  channel,
  contact,
  campaign,
  disabled = false,
}: TOFUSimulateStepAddConversationItemProps) {
  const theme = useTheme()
  const { selectedAgent } = useGetAgentCentralized()
  const agentId = selectedAgent?.[EAgentKeys.id] || ''
  const [messageContent, setMessageContent] = useState<string>('')
  const { mutate: simulateOfuCampaignMessages, isPending } =
    useOfuSimulateMessages()
  const isDisabled = isPending || disabled

  const handleSaveAddMessage = () => {
    if (!messageContent.trim()) return
    if (!agentId) {
      toast.error('Agent ID is required')
      return
    }
    const localMessages = messageContent
    const userMessageDate = new Date().toISOString()

    handleSave([
      {
        role: ESpeaker.user,
        content: localMessages,
        date: userMessageDate,
      },
    ])

    setMessageContent('')

    simulateOfuCampaignMessages(
      {
        agentId,
        messages,
        channel,
        incomingMessage: localMessages,
        contact,
        campaign,
      },
      {
        onSuccess: (data) => {
          const { responseMessage } = data as TOfuSimulateContactMessages

          handleSave([
            {
              role: ESpeaker.user,
              content: localMessages,
              date: userMessageDate,
            },
            {
              role: ESpeaker.assistant,
              content: responseMessage,
              date: new Date().toISOString(),
            },
          ])
          setMessageContent('')
        },
        onError: (error) => {
          toast.error(error?.message ?? 'Something went wrong')
        },
      },
    )
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Enter') return

    if (e.shiftKey) {
      return
    }

    e.preventDefault()
    handleSaveAddMessage()
  }

  return (
    <>
      {isPending && (
        <Skeleton
          variant="rounded"
          sx={{
            width: '80%',
            height: rem(60),
            marginLeft: 'auto',
            borderRadius: rem(16),
            borderBottomRightRadius: 0,
          }}
        />
      )}

      <Stack
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
          borderRadius: rem(8),
          cursor: isDisabled ? 'not-allowed' : 'auto',
          ...(isDisabled && {
            '& *': { cursor: 'not-allowed' },
          }),
        }}
      >
        <TextField
          maxRows={2}
          multiline
          value={messageContent}
          onChange={(e) => setMessageContent(e.target.value)}
          disabled={isDisabled}
          placeholder="Type user response..."
          hiddenLabel
          variant="standard"
          onKeyDown={handleKeyDown}
          InputProps={{
            disableUnderline: true,
            endAdornment: (
              <IconButton onClick={handleSaveAddMessage} disabled={isDisabled}>
                <KeyboardReturn />
              </IconButton>
            ),
          }}
          sx={{
            padding: { xxxs: 1 },
            borderRadius: 2,
            flexGrow: 1,
            ...(isDisabled && {
              '& .MuiInputBase-input': {
                WebkitTextFillColor: theme.palette.tertiary4,
              },
            }),
          }}
        />
      </Stack>
    </>
  )
}
